/**
 * Minimal inline-SVG sparkline. No external chart dependency.
 * - Renders a smooth polyline of the provided data
 * - Optionally fills the area beneath the line with a soft gradient
 * - Draws a dot on the last point for the "current" moment
 */
const Sparkline = ({
  data = [],
  width = 96,
  height = 32,
  color = '#a855f7',
  strokeWidth = 1.75,
  fill = true,
  className = '',
  ariaLabel = 'trend sparkline',
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className={className} style={{ width, height }} aria-hidden />;
  }

  // Handle constant series (all identical values) — draw a flat baseline
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pad = 2;

  const stepX = (width - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - (v - min) / span);
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${height - pad} L ${points[0].x.toFixed(2)} ${height - pad} Z`;

  const gradientId = `sparkline-gradient-${color.replace(/[^a-z0-9]/gi, '')}`;
  const last = points[points.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      className={className}
      data-testid="sparkline"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={areaPath} fill={`url(#${gradientId})`} />}
      <path d={linePath} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r={strokeWidth + 0.75} fill={color} />
    </svg>
  );
};

export default Sparkline;
